import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { isToday } from 'date-fns';
import { ReactElement, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Button from '~/ui/components/common/Button';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import NavigationConfirmModal from '~/ui/components/common/NavigationConfirmModal';
import MembersFields from '../MembersFields';

import { useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { ITeamMemberMapped, prepareDefaultValues } from '~/utils/clientAllocation';
import { getLocalDateNoFormat } from '~/utils/date/date';
import useClientAllocationAvailability from '../hooks/useClientAllocationAvailability';
import useEditClientAllocation from '../hooks/useEditClientAllocation';
import useFormStorage from '../hooks/useFormStorage';
import { getActiveMembers, getAssignedClientsFromFormValues, getClientsToRender } from './helpers';

import {
  IAllocationClientsByGroup,
  IClientAllocationDetailed,
} from '~/services/api/clientAllocation/types';
import { EDIT_DRAFT_ALLOCATION_KEY } from '~/services/localStorage/allocations';
import { IClientOptionByTeam } from '~/store/client/types';
import { IOption } from '~/types';
import { NETWORK } from '~/ui/constants/paths';
import { AllocationMode } from '../constants/allocationMode';
import { getViewAllocationPath } from '../helpers/getLocationPath';

import styles from './EditClientAllocation.module.scss';

interface IProps {
  teamMembers: ITeamMemberMapped[];
  clients: IOption[];
  clientAllocation: IClientAllocationDetailed[];
  actTeamId: string;
  assignedClients?: number[];
  clinicId: string;
  locationGroups: IAllocationClientsByGroup[];
  isLocationGroupAllocation: boolean;
  isCrisis?: boolean;
}

const EditClientAllocationForm = ({
  clientAllocation,
  teamMembers,
  clients,
  actTeamId,
  clinicId,
  locationGroups,
  isLocationGroupAllocation,
  isCrisis,
}: IProps): ReactElement => {
  const [isAdding, setIsAdding] = useState(false);
  // * details used to transfer data from form to modal
  const [details, setDetails] = useState<Record<string, unknown> | null>(null);
  const [withClearDraft, setWithClearDraft] = useState(false);

  const navigate = useNavigate();

  const { dateAvailability } = useStoreState(state => state.clientAllocation);

  const { isActTeam, isActTeamMember, isGlobalUser, isActTeamLeader } = useRole();

  const defValues = {
    date: clientAllocation[0]?.date,
    allocationMode: isLocationGroupAllocation
      ? AllocationMode.BY_LOCATION_GROUP
      : AllocationMode.BY_CLIENT,
    ...prepareDefaultValues(clientAllocation),
  };

  const form = useForm({
    defaultValues: defValues,
  });
  const {
    formState: { errors, isDirty, dirtyFields },
    watch,
    handleSubmit,
  } = form;

  const { isRestored, clearDraft } = useFormStorage(
    form,
    {
      key: EDIT_DRAFT_ALLOCATION_KEY,
      conditionKey: 'date',
    },
    defValues,
  );

  const formData = watch();
  const { allocationMode } = formData;
  const formValues = Object.fromEntries(
    Object.entries(formData).filter(([key]) => key !== 'date' && key !== 'allocationMode'),
  ) as Record<string, unknown>;

  const isDisabledCreateButton =
    !!dateAvailability?.clients.length && dateAvailability?.clients.some(c => !c.canBeCreated);

  const newAssignedClients = formValues as Record<string, number[]>;

  const { list, current } = useStoreState(store => store.client.clientOptionsByTeam);

  const selectedClientOptions: IClientOptionByTeam[] = useMemo(() => {
    const clientIds = Object.keys(newAssignedClients).reduce((acc, key) => {
      if (key.startsWith('clients_') && newAssignedClients[key]) {
        const newClients = newAssignedClients[key] || [];
        acc.push(...newClients);
      }
      return acc;
    }, [] as number[]);
    return list.filter(item => clientIds.includes(item.id));
  }, [list, newAssignedClients]);

  const onSubmitHandler = useEditClientAllocation({
    clinicId,
    isGlobalUser,
    isActTeamMember,
    clientAllocation,
    actTeamId,
    current: selectedClientOptions,
    locationGroups,
    dirtyFields,
    isCrisis,
  });

  const onSubmit = async (values: Record<string, any>) => {
    setIsAdding(true);

    await onSubmitHandler(values);

    setIsAdding(false);
    setDetails(null);
    navigate(NETWORK);
    clearDraft();
  };

  const openConfirmationModal = (values: Record<string, unknown>) => {
    const { date } = values;
    if (
      getLocalDateNoFormat(date as string) < new Date() &&
      !isToday(getLocalDateNoFormat(date as string))
    ) {
      setDetails(values);
      return;
    }
    onSubmit(values);
  };

  const handleCancel = () => {
    setWithClearDraft(true);
    if (isGlobalUser || isActTeam) {
      const finalRoute = getViewAllocationPath(isCrisis, isGlobalUser)
        .replace(':actTeamId', actTeamId)
        .replace(':clientAllocationDate', clientAllocation[0].date.split('T')[0]);
      navigate(finalRoute);
    } else navigate(NETWORK);
  };

  const activeMembers = useMemo(
    () => getActiveMembers(teamMembers, clientAllocation, isCrisis),
    [teamMembers, clientAllocation, isCrisis],
  );

  // List of all clients
  const clientsToRender = useMemo(
    () => getClientsToRender(isGlobalUser, clients, current),
    [isGlobalUser, clients, current],
  );

  const clientAllocationClientsIds = useMemo(
    () => clientAllocation.flatMap(item => item.user.clients.map(c => c.id)),
    [clientAllocation],
  );
  const assignedClients = useMemo(
    () => getAssignedClientsFromFormValues(formValues as Record<string, number[] | number>),
    [formValues],
  );

  useClientAllocationAvailability({
    dateRange: {
      from: clientAllocation[0]?.date,
      to: clientAllocation[0]?.date,
    },
    clientsList: assignedClients,
    assignedTeams: {},
    clientAllocationClientsIds,
    clinicId,
    teamId: Number(actTeamId),
  });

  const isDisabledSaveButton = useMemo(
    () =>
      Object.keys(errors).length > 0 ||
      isDisabledCreateButton ||
      clientAllocation.every(item => !!item.resolved),
    [errors, isDisabledCreateButton, clientAllocation],
  );

  const leavePageDescription =
    'Are you sure you want to cancel Edit Client Allocation"? All the changes will be discarded.';

  const editPastAllocationDescription =
    'Caution. You are modifying a past date. Are you sure you want to proceed?';

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(openConfirmationModal)}>
        <Paper>
          <Box p={2}>
            <Grid container spacing={4}>
              <Grid item sm={12}>
                <Typography variant="subtitle1" className={styles.assignedClientsTitle}>
                  {allocationMode === AllocationMode.BY_CLIENT
                    ? 'Assigned Clients per Team Member'
                    : 'Location Groups per Team Member'}
                </Typography>
              </Grid>
            </Grid>

            {isRestored && (
              <Grid container spacing={3} className={styles.formWrapper}>
                <MembersFields
                  members={activeMembers}
                  clients={clients}
                  clientAllocationDate={clientAllocation[0]?.date}
                  clientAllocations={clientAllocation}
                  isGlobalUser={isGlobalUser}
                  assignedClients={newAssignedClients}
                  clinicId={clinicId}
                  clientsList={clientsToRender as IClientOptionByTeam[]}
                  locationGroups={locationGroups}
                  isActTeamLeader={isActTeamLeader}
                  validateAvailability
                  isEdit
                  isCrisis={isCrisis}
                />
              </Grid>
            )}
          </Box>
        </Paper>

        <div className={styles.buttons}>
          <div className={styles.buttonWrapper}>
            <Button color="primary" variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
          <Button color="primary" variant="contained" type="submit" disabled={isDisabledSaveButton}>
            Save Changes
          </Button>
        </div>

        <NavigationConfirmModal
          when={isDirty && !isAdding}
          description={leavePageDescription}
          beforeConfirm={withClearDraft ? clearDraft : undefined}
        />
        {/* this modal triggers only when allocation date is in the past */}
        {details && (
          <ConfirmModal
            onConfirm={() => {
              onSubmit(details as Record<string, any>);
            }}
            onClose={() => setDetails(null)}
            description={editPastAllocationDescription}
            confirmText="Proceed"
          />
        )}
      </form>
    </FormProvider>
  );
};

export default EditClientAllocationForm;
